import type { DocumentType, Prisma, Role } from '@prisma/client';
import type { PermissionKey } from './permission.schema';

// Document types that carry a customer-facing $ value, hidden from non-admins by
// Business.isFinancialAttachmentViewableByTech (see documentVisibilityWhere).
export const FINANCIAL_DOCUMENT_TYPES: DocumentType[] = ['QUOTE', 'SIGNED_QUOTE', 'INVOICE', 'BILLING', 'RECEIPT', 'PROPOSAL'];

/**
 * The single predicate every document read must apply.
 *
 * isFinancialAttachmentViewableByTech is enforced here rather than baked into Document.allowedRoles at
 * creation, which keeps it subtractive: it can only ever remove rows, so it can never widen visibility
 * past isAttachmentViewableByTech, and it applies to documents that already existed when the setting
 * was changed. As a consequence the setting is a hard ceiling — it also hides financial documents an
 * admin has manually marked technician-viewable.
 *
 * Compose under `AND`; this constrains `documentType`, which several callers also constrain.
 *
 * `role` is required rather than optional because Prisma drops `has: undefined` from the filter,
 * which would return every document in the business.
 */
export const documentVisibilityWhere = ({
	isFinancialAttachmentViewableByTech,
	role,
}: {
	isFinancialAttachmentViewableByTech: boolean;
	role: Role;
}): Prisma.DocumentWhereInput => {
	const hideFinancialDocuments = role !== 'FULL' && !isFinancialAttachmentViewableByTech;

	return {
		allowedRoles: { has: role },
		...(hideFinancialDocuments ? { documentType: { notIn: FINANCIAL_DOCUMENT_TYPES } } : {}),
	};
};

// A Document in the Document Hub won't be related to these entities, a new copy would be made.
// Typed so dropping a Document column turns every usage into a compile error.
//
// This is the single definition of hub membership, and the copy-into-job guards spread it rather than
// restating a subset: those take document ids straight from the client, so any predicate they omit is a way
// to copy a document the hub does not list.
//
// mailboxMessageId is here for a different reason than the rest. Downloading an email attachment from a
// thread that is not linked to a job stores it with every FK null (mailbox.service.ts downloadAttachment
// passes storeOnJob: false and sets only documentType + mailboxMessageId), which is indistinguishable from a
// hub upload by the other columns. Those files were never uploaded to the hub, and surfacing them there put
// one customer's correspondence in the picker used to attach files to another customer's submittal.
export const DOCUMENT_HUB_ISOLATION = {
	customTaskId: null,
	dailyReportId: null,
	estimateId: null,
	installationId: null,
	jobId: null,
	jobLogId: null,
	mailboxMessageId: null,
	measurementId: null,
	pinId: null,
	proposalContentBlockId: null,
	purchaseOrderId: null,
	quoteId: null,
	quoteRequestId: null,
	submittalId: null,
	supplierInvoiceId: null,
} satisfies Prisma.DocumentWhereInput;

export type DocumentScope = { jobId: number; kind: 'job' } | { kind: 'hub' };

export const DOCUMENT_SCOPE_PERMISSIONS = {
	hub: 'MENU_DOCUMENT_HUB',
	job: 'JOB_MENU_ATTACHMENTS',
} as const satisfies Record<DocumentScope['kind'], `${PermissionKey}`>;

export const canOpenDocuments = (role: unknown): role is 'FULL' | 'TECHNICIAN' => role === 'FULL' || role === 'TECHNICIAN';

export const documentAccessWhere = ({
	allowedPermissions,
	isFinancialAttachmentViewableByTech,
	role,
	scope,
}: {
	allowedPermissions: readonly string[];
	isFinancialAttachmentViewableByTech: boolean;
	role: Role;
	scope?: DocumentScope;
}): Prisma.DocumentWhereInput | null => {
	if (!canOpenDocuments(role)) return null;

	const scopes: Prisma.DocumentWhereInput[] = [
		...(allowedPermissions.includes(DOCUMENT_SCOPE_PERMISSIONS.hub) && scope?.kind !== 'job' ? [DOCUMENT_HUB_ISOLATION] : []),
		...(allowedPermissions.includes(DOCUMENT_SCOPE_PERMISSIONS.job) && scope?.kind !== 'hub'
			? [{ jobId: scope?.kind === 'job' ? scope.jobId : { not: null } }]
			: []),
	];
	if (scopes.length === 0) return null;

	return { AND: [documentVisibilityWhere({ isFinancialAttachmentViewableByTech, role }), { OR: scopes }] };
};
